import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

import { Box } from '@mui/material';

export const BBSelect = ({
  options,
  value,
  placeholder,
  isMulti,
  isLoading,
  isDisabled,
  isClearable = true,
  isSearchable = true,
  isFullWidth = false,
  labelPrefix,
  menuPlacement,
  inputHeight = '40px',
  onChange,
  sx,
  selectStyles: selectStylesOverride,
  'aria-label': ariaLabel,
  inputId,
}) => {
  const customStyles = useMemo(() => {
    const base = {
      control: (provided) => ({
        ...provided,
        background: '#FFF',
        borderColor: isDisabled ? '#DDD' : '#C4C4C4',
        minHeight: inputHeight,
        height: inputHeight,
        cursor: 'pointer',
        '&:hover': {
          borderColor: isDisabled ? '#DDD' : '#000',
        },
      }),
      placeholder: (provided) => ({
        ...provided,
        color: isDisabled ? '#00000061' : '#181D1F',
        fontSize: '14px',
      }),
      singleValue: (provided) => ({
        ...provided,
        color: isDisabled ? '#00000061' : '#181D1F',
        fontSize: '14px',
      }),
      multiValue: (provided) => ({
        ...provided,
        color: '#181D1F',
        fontSize: '16px',
        maxWidth: '120px',
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: inputHeight,
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: inputHeight,
      }),
      menu: (provided) => ({
        ...provided,
        color: '#181D1F',
        fontSize: '14px',
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };

    if (!selectStylesOverride) return base;

    // Merge overrides: each key's function wraps the base function
    const merged = { ...base };
    Object.keys(selectStylesOverride).forEach((key) => {
      const baseFn = base[key];
      const overrideFn = selectStylesOverride[key];
      merged[key] = (provided, state) => {
        const baseResult = baseFn ? baseFn(provided, state) : provided;
        return overrideFn(baseResult, state);
      };
    });
    return merged;
  }, [isDisabled, inputHeight, selectStylesOverride]);

  const selectedValue = useMemo(() => {
    if (isMulti && Array.isArray(value)) {
      return value?.map((v) => options?.find((option) => option.value === v));
    }
    return options?.find((option) => option.value === value);
  }, [options, value, isMulti]);

  const formatOptionLabel = useMemo(() => {
    if (!labelPrefix) return undefined;

    return (option, { context }) => {
      // In the menu dropdown, show just the option label
      if (context === 'menu') {
        return option.label;
      }
      // In the control (selected value), show with prefix
      return `${labelPrefix}: ${option.label}`;
    };
  }, [labelPrefix]);

  return (
    <Box
      sx={{
        minWidth: isFullWidth ? '100%' : '200px',
        maxWidth: isMulti ? 'fit-content' : '200px',
        '&:hover': {
          cursor: isDisabled ? 'not-allowed !important' : 'unset',
        },
        color: 'red',
        ...sx,
      }}
    >
      <Select
        styles={customStyles}
        closeMenuOnSelect={!isMulti}
        value={selectedValue}
        menuPortalTarget={document.body}
        formatOptionLabel={formatOptionLabel}
        aria-label={ariaLabel}
        inputId={inputId}
        {...{
          options,
          placeholder,
          isMulti,
          isLoading,
          isDisabled,
          isClearable,
          isSearchable,
          menuPlacement,
          onChange,
        }}
      />
    </Box>
  );
};

BBSelect.propTypes = {
  options: PropTypes.any,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  labelPrefix: PropTypes.string,
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  sx: PropTypes.object,
  selectStyles: PropTypes.object,
  onChange: PropTypes.func,
  'aria-label': PropTypes.string,
  inputId: PropTypes.string,
};
